import React, { useCallback, useState } from 'react';

import { Button } from '@/components/design-system/Button';
import { cn } from '@/utils/cn';
import { SlidersOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface FilteredTableLayoutProps {
  filters: React.ReactNode;
  tableHeader: React.ReactNode;
  table: React.ReactNode;
  isFiltersCollapsed?: boolean;
  onToggleFiltersCollapse?: () => void;
}

export const FilteredTableLayout: React.FC<FilteredTableLayoutProps> = ({
  filters,
  tableHeader,
  table,
  isFiltersCollapsed: controlledIsFiltersCollapsed,
  onToggleFiltersCollapse: controlledOnToggleFiltersCollapse
}) => {
  const { t } = useTranslation();

  const [localIsFiltersCollapsed, setLocalIsFiltersCollapsed] = useState(true);

  const handleLocalToggle = useCallback(() => {
    setLocalIsFiltersCollapsed((prev) => !prev);
  }, []);

  const isFiltersCollapsed = controlledIsFiltersCollapsed ?? localIsFiltersCollapsed;
  const onToggleFiltersCollapse = controlledOnToggleFiltersCollapse ?? handleLocalToggle;
  return (
    <div className="flex h-full">
      <div
        className={cn(
          'border-border-r bg-background-700 border transition-all duration-100',
          isFiltersCollapsed ? 'w-0 overflow-hidden' : 'w-[240px]'
        )}
      >
        <div className="h-full w-full pb-2">
          <span className="text-typography-900 flex h-16 items-center pl-4 text-lg font-medium">
            <SlidersOutlined className="mr-2" />
            {t('label.filters')}
          </span>
          <div className="h-[calc(100%-64px)] overflow-y-auto pt-2">{filters}</div>
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="border-border flex h-16 items-center justify-between border-b px-4">
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="md"
              onClick={onToggleFiltersCollapse}
              aria-label={isFiltersCollapsed ? 'Show filters' : 'Hide filters'}
              data-testid="pms-show-filter"
            >
              <SlidersOutlined className="mr-1" />
              <VerticalAlignTopOutlined
                className={cn('transition-transform duration-300', isFiltersCollapsed ? 'rotate-90' : 'rotate-270')}
              />
            </Button>
            {tableHeader}
          </div>
        </div>

        <div className="bg-background h-full flex-1 overflow-y-auto p-2">{table}</div>
      </div>
    </div>
  );
};
